import { useApolloClient, useQuery } from '@apollo/client';
import { GeoJsonLayerProps } from '@deck.gl/layers/typed';
import { Box, DialogActions, FormHelperText, Grid, styled } from '@mui/material';
import {
  circle,
  Feature,
  FeatureCollection,
  featureCollection,
  geometry,
  multiPolygon,
  MultiPolygon,
  Point,
  pointOnFeature,
  polygon,
  Polygon,
} from '@turf/turf';
import FlexBox from 'Components/FlexBox';
import { DialogDefault, Skeleton, ThemeButton, ThemeTypography } from 'DesignSystem';
import GeoDataField from 'DesignSystem/Formik/GeoDataField/GeoDataField';
import { ICustomMapRef, IMapPopup } from 'DesignSystem/Map/CustomMap';
import { radiusFromHectares, toMapFeature } from 'DesignSystem/Map/utils/map.utils';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import PlotInfoPopup, { IFeatureProperties } from 'FeatureModules/Site/Components/PlotInfoPopup';
import usePlotsMutations from 'FeatureModules/Site/hooks/usePlotMutations';
import { generatePlotSchema, IPlotValues } from 'FeatureModules/Site/schemas/sites.schema';
import { IPlot, TGeometry } from 'FeatureModules/Site/types/plot.types';
import { IFarmSite } from 'FeatureModules/Site/types/site.types';
import { ErrorMessage, Field, Form, Formik, FormikProps } from 'formik';
import { BASE_SITE_FRAGMENT } from 'graphql/secure/fragments/site.fragments';
import { GET_NEIGHBOURING_PLOTS } from 'graphql/secure/queries/plot.queries';
import { INeighbouringPlotsInput } from 'graphql/secure/queries/types/plot-query.types';
import useDialog from 'hooks/useDialog';
import useMessages from 'hooks/useMessages';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import theme from 'styles/theme';
import { IGraphQlConnection } from 'types/core.types';
import { IDefaultDialogProps } from 'types/dialog.types';
import { convertHexToRGBarray } from 'utils/color.utils';
import { mergeDefaults } from 'utils/form.utils';
import { removeGraphConnections } from 'utils/graphql.utils';
import { v4 as uuid } from 'uuid';
import PlotBaseForm from '../Forms/PlotBaseForm';

type IAddEditFarmPlotDialogProps = IDefaultDialogProps & {
  /**
   * Default view of the dialog, can be 'map' or 'json'
   * @default is 'map'
   */
  defaultView?: 'map' | 'json';
  geoDataId?: string;
  onPlotsUpdated?: () => void;
} & ({ plot: IPlot; farmId?: never } | { plot?: never; farmId: string });

const TopBackground = styled('div')(({ theme }) => ({
  '&::before': {
    content: '""',
    position: 'absolute',
    zIndex: 0,
    top: 0,
    left: 0,
    right: 0,
    height: '42%',
    background: theme.custom.colors.backgroundLight,
  },
}));

/**
 * Dialog for adding a new plot or editing an existing plot
 * - When `plot` is provided: Edit mode - updates existing plot
 * - When `farmId` is provided: Create mode - creates new plot for the farm
 */
const AddEditFarmPlotDialog: FC<IAddEditFarmPlotDialogProps> = ({
  open,
  plot,
  farmId,
  geoDataId,
  defaultView,
  onPlotsUpdated,
  onClose,
}) => {
  const resolvedFarmId = plot?.farmId || farmId;
  const client = useApolloClient();

  const { data: neighbouringFarmData, loading: neighbouringPlotsLoading } = useQuery<
    { neighbouringPlots: IGraphQlConnection<IPlot> },
    { input?: INeighbouringPlotsInput }
  >(GET_NEIGHBOURING_PLOTS, {
    variables: resolvedFarmId
      ? // If edit and the plot is provided, show all neighbouring plots otherwise use the farmId to show neighbouring plots
        { input: { ...(plot ? { plotId: plot?.id } : { farmId }), geoDataId } }
      : undefined,
    skip: !resolvedFarmId,
  });

  // Derive farm data from the plot otherwise from the cache using the farmId
  const farm = resolvedFarmId
    ? client.readFragment<IFarmSite>({
        fragment: BASE_SITE_FRAGMENT,
        id: `Site:${resolvedFarmId}-en`,
        fragmentName: 'baseSiteValues',
      })
    : null;

  const loading = false; // Since we have the plot data already
  const plotSchema = generatePlotSchema(!!farmId);

  const { setErrorMessage, setSuccessMessage } = useMessages();
  const { createPlots, deletePlots, updatePlots, updateLoading, createLoading, deleteLoading } =
    usePlotsMutations();
  const updateCreateLoading = updateLoading || createLoading;

  const { openDialog } = useDialog();
  const formRef = useRef<FormikProps<IPlotValues> | null>(null);
  const customMapRef = useRef<ICustomMapRef>(null);

  const [infoPopup, setInfoPopup] = useState<IMapPopup>();

  const neighbouringPlots: IPlot[] = useMemo(() => {
    if (neighbouringFarmData?.neighbouringPlots) {
      return removeGraphConnections(neighbouringFarmData.neighbouringPlots);
    }
    return [];
  }, [neighbouringFarmData]);

  const initialValues: IPlotValues = mergeDefaults(plotSchema.default(), {
    title: plot?.title,
    size: plot?.sizeHa,
    plotId: plot?.externalId,
    farmId: plot?.farm.externalId,
    feature: plot?.feature ? toMapFeature(plot.feature.geometry, plot.id, plot.externalId) : null,
    isAgroforestry: plot?.isAgroforestry,
  });

  /**
   * This feature collections contains all the cultivation areas of the farm as single polygons with their properties
   * To be able to show the info popup when clicking on the cultivation area on a single polygon we need to have the center of each polygon
   * To be able to show centered markers of the one cultivation area we need to have the center of the multi polygon
   */
  const neighbouringPolygonFeatures: FeatureCollection<
    Polygon | Point | MultiPolygon,
    IFeatureProperties
  > = useMemo(() => {
    const features: Feature<Polygon | Point | MultiPolygon, IFeatureProperties>[] =
      neighbouringPlots.map(plot => {
        const feature = plot.feature;
        const featureGeometry = geometry(feature.geometry.type, feature.geometry.coordinates);
        const properties: IFeatureProperties = {
          plot,
          // Center of the polygon always inside the polygon
          center:
            feature.geometry.type === 'Point'
              ? feature.geometry.coordinates
              : (pointOnFeature(featureGeometry).geometry.coordinates as [number, number]),
          isNeighbouringPlot: true,
        };
        if (feature.geometry.type === 'Polygon') {
          return polygon(feature.geometry.coordinates, properties, { id: uuid() });
        } else if (feature.geometry.type === 'MultiPolygon') {
          return multiPolygon(feature.geometry.coordinates, properties, { id: uuid() });
        } else if (feature.geometry.type === 'Point') {
          return circle(
            feature.geometry.coordinates,
            // For now we use a circle with a radius of 112.8 meters which has 4ha area
            plot.sizeHa ? radiusFromHectares(plot.sizeHa) : 112.8,
            {
              units: 'meters',
              properties,
            }
          );
        }
        throw new Error(
          `Plot with ID ${plot.id} has an invalid geometry type: ${feature.geometry}`
        );
      });
    return featureCollection(features);
  }, [neighbouringPlots]);

  const handleClose = () => {
    if (formRef.current && !isEqual(formRef.current.initialValues, formRef.current.values)) {
      openDialog({
        type: 'ALERT',
        props: {
          title: 'Unsaved changes',
          text: 'Are you sure you want to close this window? All unsaved changes will be lost and you will not be able to undo this action.',
          submitText: 'Discard changes',
          itemTitle: plot?.title || 'Unnamed plot',
          displayCloseButton: true,
          onSubmit: onClose,
          onCancel: () => undefined,
        },
      });
    } else {
      onClose?.();
    }
  };

  const handleFeatureClick = useCallback(
    (featureProperties: IFeatureProperties) => {
      setInfoPopup(prev => {
        if (prev && prev.id === featureProperties.plot.id) {
          return undefined;
        }
        return {
          id: featureProperties.plot.id,
          coordinate: featureProperties.center,
          content: <PlotInfoPopup infoItem={featureProperties} />,
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [infoPopup, setInfoPopup]
  );

  const neighbouringLayerProps: GeoJsonLayerProps = useMemo(
    () => ({
      id: 'neighbouring-plots-map-layer',
      data: neighbouringPolygonFeatures,
      getLineWidth: 2,
      pickable: true,
      getPointRadius: 4,
      getFillColor: () => convertHexToRGBarray(theme.custom.themeColors.grayScale[40], 60),
      getLineColor: () => convertHexToRGBarray(theme.custom.themeColors.grayScale[40]),
      onClick: info => handleFeatureClick(info.object.properties),
    }), // eslint-disable-next-line react-hooks/exhaustive-deps
    [neighbouringPolygonFeatures, handleFeatureClick]
  );

  /**
   * Handle changes to the plot - supports both creation and updates
   */
  const handleSubmit = async (values: IPlotValues) => {
    try {
      const isEditMode = !!plot;

      if (isEditMode) {
        // Edit mode: Update existing plot
        const hasGeometryChanged = !isEqual(
          values.feature.geometry,
          omit(plot.feature.geometry, '__typename')
        );

        if (
          !hasGeometryChanged &&
          formRef.current &&
          isEqual(formRef.current.initialValues, formRef.current.values)
        ) {
          onClose?.();
          return;
        }

        await updatePlots({
          variables: {
            inputs: [
              {
                id: plot.id,
                title: values.title,
                // Only update point size if the geometry is a point
                pointSizeHa: values.feature.geometry.type === 'Point' ? values.size : null,
                externalId: values.plotId,
                geometry: hasGeometryChanged ? (values.feature.geometry as TGeometry) : undefined,
                isAgroforestry: values.isAgroforestry ?? null,
              },
            ],
          },
        });

        setSuccessMessage('Plot successfully updated');
      } else {
        // Create mode: Create new plot
        if (!farmId) {
          setErrorMessage('Farm ID is required to create a new plot.');
          return;
        }

        await createPlots({
          variables: {
            inputs: [
              {
                farmId,
                geometry: values.feature.geometry as TGeometry,
                title: values.title,
                // Only send point size if the geometry is a point
                pointSizeHa: values.feature.geometry.type === 'Point' ? values.size : null,
                externalId: values.plotId,
                isAgroforestry: values.isAgroforestry ?? null,
              },
            ],
          },
        });

        setSuccessMessage('Plot successfully created');
      }

      onClose?.();
      onPlotsUpdated?.();
    } catch (error) {
      console.error(error);
      setErrorMessage(
        `An unknown error occurred while ${plot ? 'updating' : 'creating'} the plot.`
      );
    } finally {
      formRef.current?.setSubmitting(false);
    }
  };

  const handleDeletePlot = () => {
    if (!plot) return;
    openDialog({
      type: 'ALERT',
      props: {
        title: 'Delete plot',
        text: 'Are you sure you want to delete this plot? All data will be lost and you will not be able to recover the plot.',
        submitText: 'Delete',
        itemTitle: `${plot.externalId ? `${plot.externalId} | ` : ''} ${plot.title}`,
        displayCloseButton: true,
        onSubmit: () => {
          deletePlots({
            variables: { ids: [plot.id] },
            onCompleted: () => {
              setSuccessMessage('Plot successfully deleted');
            },
            onError: () => {
              setErrorMessage('Failed to delete plot');
            },
          });
          onClose?.();
          onPlotsUpdated?.();
        },
        onCancel: () => undefined,
      },
    });
  };

  return (
    <DialogDefault
      title={plot ? 'Edit Plot' : 'Add Plot'}
      itemTitle={plot?.title}
      onClose={handleClose}
      open={open}
      fullWidth
      iconName="polygon-mapping"
      maxWidth="xl"
    >
      <TopBackground />
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        validateOnChange
        validationSchema={plotSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form style={{ width: '100%', zIndex: 0 }}>
            <Grid container spacing={3} px={3}>
              <Grid item xs={12} md={6}>
                <Box display="flex" pt={2} flexDirection="column">
                  <PlotBaseForm />
                  <Box mt={1}>
                    <ErrorMessage name="feature">
                      {msg => <FormHelperText error>{msg}</FormHelperText>}
                    </ErrorMessage>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <FlexBox
                  alignItems="flex-start"
                  flexDirection="column"
                  height="60vh"
                  minWidth={400}
                  width="100%"
                  gap={2}
                >
                  <FlexBox gap={0.5}>
                    <Icon name="site-location-point" size="small" color="light-violet" />
                    <ThemeTypography variant="BODY_MEDIUM" autoOverflow maxWidth={500}>
                      <b>{farm?.title || 'Farm'}</b>
                      {farm?.externalId ? `: ${farm.externalId}` : null}
                    </ThemeTypography>
                    <ThemeTypography variant="LABEL_INPUT" autoOverflow maxWidth={200}>
                      ({farm?.locationName})
                    </ThemeTypography>
                  </FlexBox>

                  {/* We can only show the map if all plots are loaded */}
                  {loading ? (
                    <Box height="100%" display="flex" flexDirection="column" gap={2}>
                      <Skeleton variant="rounded" width="100%" height={'calc(100% - 64px)'} />
                    </Box>
                  ) : (
                    <Field
                      name="feature"
                      areaFieldName="size"
                      type="feature"
                      lightBackground
                      infoPopup={infoPopup}
                      customMapRef={customMapRef}
                      geoJsonLayerLoading={neighbouringPlotsLoading}
                      geoJsonLayerProps={neighbouringLayerProps}
                      geoJsonLayerLabel="Show neighbouring farm plots on map (in grey)."
                      component={GeoDataField}
                      defaultView={defaultView}
                      marker={farm?.locationCoordinates}
                      defaultCenter={farm?.locationCoordinates}
                      onMarkerPopupClose={() => setInfoPopup(undefined)}
                    />
                  )}
                </FlexBox>
              </Grid>
            </Grid>

            <DialogActions>
              <FlexBox width="100%" justifyContent="space-between">
                {plot ? (
                  <ThemeButton
                    color="BLUE_ICE"
                    size="large"
                    type="button"
                    disabled={loading || isSubmitting || updateCreateLoading}
                    loading={deleteLoading}
                    onClick={handleDeletePlot}
                  >
                    Delete plot
                  </ThemeButton>
                ) : (
                  <Box />
                )}

                <FlexBox gap={2}>
                  <ThemeButton color="BLUE_ICE" size="large" type="button" onClick={handleClose}>
                    Cancel
                  </ThemeButton>

                  <ThemeButton
                    loading={updateCreateLoading || isSubmitting}
                    color="PRIMARY"
                    size="large"
                    type="submit"
                    disabled={loading || deleteLoading}
                  >
                    {plot ? 'Save' : 'Create'}
                  </ThemeButton>
                </FlexBox>
              </FlexBox>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </DialogDefault>
  );
};

export default AddEditFarmPlotDialog;
